import type {
	ColumnConfiguration,
	FieldConfiguration,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../types';
import { salesTrendFields } from './competitor/columns';

/**
 * PD-17066 — a competitor's Customers tab: the customers linked to it, with
 * the relationship, product groups, estimated spend with them, our 12-month
 * sales to the customer and when the link was last confirmed. Rows are
 * `client_wholesaler` links from GET /api/competitors/:id/customers, limited
 * to customers the caller may open. Sub-tabs lock the relationship.
 */
const TABLE = 'client_wholesaler';

export const COMPETITOR_RELATIONSHIP_VALUE_MAPPING = [
	{ value: 'primary', label: 'Primary', color: { type: 'info' } },
	{ value: 'secondary', label: 'Secondary', color: { type: 'neutral' } },
	{ value: 'also_buys', label: 'Also buys from', color: { type: 'neutral' } },
	{ value: 'former', label: 'Former', color: { type: 'warning' } },
];

const lineBreak = (
	id: string,
	visibleWhen?: FieldConfiguration['conditionalVisibility'],
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
	...(visibleWhen != null ? { conditionalVisibility: visibleWhen } : {}),
});

const secondary = (id: string, fieldName: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: TABLE } },
	styleConfig: {
		size: 'sm',
		colorRule: { type: 'theme', themeColor: 'textSecondary' },
	},
	displayStyle: 'text',
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
});

/** Relationship, spend, product groups, confirmation and note columns shared with the customer's tab. */
export const competitorLinkDetailColumns = (): Array<ColumnConfiguration> => [
	{
		id: 'link-relationship',
		fields: [
			{
				id: 'link-relationship-badge',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'relationship', objectTable: TABLE },
				},
				styleConfig: {
					size: 'xs',
					valueMapping: COMPETITOR_RELATIONSHIP_VALUE_MAPPING,
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h-relationship', text: 'Relationship' }],
		minWidth: 130,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-product-groups',
		fields: [
			{
				id: 'link-product-groups-list',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'productGroups', objectTable: TABLE },
					defaultValue: '-',
				},
				styleConfig: { size: 'xs', colorRule: { type: 'default' } },
				displayStyle: 'badge_list',
			},
		],
		headers: [{ id: 'h-product-groups', text: 'Product Groups' }],
		minWidth: 170,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-spend',
		fields: [
			{
				id: 'link-estimated-spend',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'estimatedAnnualSpend', objectTable: TABLE },
					defaultValue: 'Not estimated',
				},
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
					formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
				},
				displayStyle: 'currency',
			},
		],
		headers: [{ id: 'h-spend', text: 'Estimated Spend With Them' }],
		minWidth: 140,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-our-sales',
		fields: [
			{
				id: 'link-our-sales-12',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'ourSales12Months', objectTable: TABLE },
					defaultValue: '-',
				},
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
					formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
				},
				displayStyle: 'currency',
			},
			lineBreak('link-our-sales-lb1', {
				field: 'ourSalesTrend',
				condition: 'is_not_null',
			}),
			...salesTrendFields('link-our-sales-trend', 'ourSalesTrend', TABLE),
		],
		headers: [{ id: 'h-our-sales', text: 'Our Sales, 12 Months' }],
		minWidth: 150,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-confirmed',
		fields: [
			{
				id: 'link-confirmed-date',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'confirmedDate', objectTable: TABLE },
					defaultValue: 'Never',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'relative_date',
			},
			lineBreak('link-confirmed-lb1', {
				field: 'confirmedByName',
				condition: 'is_not_null',
			}),
			secondary('link-confirmed-by', 'confirmedByName'),
		],
		headers: [{ id: 'h-confirmed', text: 'Last Confirmed' }],
		minWidth: 130,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-note',
		fields: [
			{
				id: 'link-note-text',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'note', objectTable: TABLE },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
				truncate: true,
				maxLength: 120,
			},
		],
		headers: [{ id: 'h-note', text: 'Note' }],
		minWidth: 180,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'link-actions',
		fields: [
			{
				id: 'link-actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'link-confirm',
						label: 'Confirm Still True',
						iconName: 'check',
						callbackName: 'confirmLink',
						requiredPermission: 'competitors:update',
					},
					{
						id: 'link-edit',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'editLink',
						requiredPermission: 'competitors:update',
					},
					{
						id: 'link-remove',
						label: 'Remove',
						iconName: 'trash',
						callbackName: 'removeLink',
						variant: 'danger',
						requiredPermission: 'competitors:update',
					},
				],
			},
		],
		headers: [{ id: 'h-link-actions', text: '' }],
		width: 80,
		sortable: false,
	},
];

const customerColumn = (): ColumnConfiguration => ({
	id: 'link-customer',
	fields: [
		{
			id: 'link-client-name',
			link: { type: 'object', enabled: true, objectTable: 'client', idField: 'clientId' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		lineBreak('link-customer-lb1', {
			field: 'clientLocation',
			condition: 'is_not_null',
		}),
		secondary('link-client-location', 'clientLocation'),
		lineBreak('link-customer-lb2', {
			field: 'salespersonName',
			condition: 'is_not_null',
		}),
		secondary('link-client-salesperson', 'salespersonName'),
	],
	headers: [{ id: 'h-customer', text: 'Customer' }],
	minWidth: 200,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

const relationshipTab = (
	id: string,
	name: string,
	relationship: string | null,
	sortOrder: number,
): ListBuilderList => ({
	id,
	cacheTTL: 600000,
	name,
	type: 'default',
	active: true,
	sortOrder,
	columns: [customerColumn(), ...competitorLinkDetailColumns()],
	...(relationship != null
		? {
				filterRules: {
					competitorLinkRelationship: {
						defaultValue: [{ id: relationship, label: name }],
						locked: true,
						hidden: true,
					},
				},
			}
		: {}),
});

export const COMPETITOR_CUSTOMERS_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		relationshipTab('default-competitor-customers-all', 'All', null, 0),
		relationshipTab('default-competitor-customers-primary', 'Primary', 'primary', 1),
		relationshipTab(
			'default-competitor-customers-secondary',
			'Secondary',
			'secondary',
			2,
		),
		relationshipTab(
			'default-competitor-customers-also-buys',
			'Also Buys From',
			'also_buys',
			3,
		),
		relationshipTab('default-competitor-customers-former', 'Former', 'former', 4),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		// Carries each sub-tab's locked relationship; the panel itself is hidden.
		activeFilters: ['competitorLinkRelationship'],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Customer Link',
			objectTable: TABLE,
			// Rows are client_wholesaler links keyed by `id`.
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'tier', fieldLabel: 'Tier' },
				{ fieldName: 'relationship', fieldLabel: 'Relationship' },
				{ fieldName: 'estimatedAnnualSpend', fieldLabel: 'Estimated Annual Spend' },
				{ fieldName: 'note', fieldLabel: 'Note' },
				{ fieldName: 'confirmedDate', fieldLabel: 'Confirmed' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Customer',
			objectTable: 'client',
			selectedFields: [{ fieldName: 'clientId', fieldLabel: 'ID' }],
		},
	],
};
